// Exact-match SEO landing pages. Rendered at /<slug> (Turkish) and
// /en/<slug> (English). Keep slugs stable, they're in the URL, in
// package.json "reactSnap.include", and in public/sitemap.xml.
//
// The two pages target near-synonym head terms ("akaryakıt istasyonu
// yazılımı" vs "benzin istasyonu yazılımı/programı"). To avoid keyword
// cannibalization each page has DISTINCT copy and a distinct angle:
// page A = comprehensive reconciliation/cash-flow software; page B =
// day-to-day shift/cash control "program" for station owners.

import { PRICE_TEXT } from "./productFacts";

export type LandingLang = "tr" | "en";

export interface LandingFeature {
  icon?: string;
  title?: string;
  text?: string;
}

export interface LandingFaq {
  question?: string;
  answer?: string;
}

/** One section of a landing page's long-form guide (see LandingLocale.guide). */
export interface LandingGuideSection {
  heading: string;
  paragraphs: string[];
  /** Optional numbered steps shown after the paragraphs. */
  steps?: string[];
  /** Optional internal link, always the final trailing-slash URL. */
  link?: { to: string; label: string };
}

/** Long-form guide rendered between the features and the FAQ. */
export interface LandingGuide {
  title: string;
  sections: LandingGuideSection[];
}

export interface LandingLocale {
  metaTitle: string;
  metaDescription: string;
  h1: string;
  heroSubtitle: string;
  intro: string;
  featuresTitle: string;
  features: LandingFeature[];
  /** Optional long-form guide (only the main head-term page uses it). */
  guide?: LandingGuide;
  faqTitle: string;
  faq: LandingFaq[];
  ctaTitle: string;
  ctaText: string;
  ctaButton: string;
  serviceName: string;
}

export interface LandingPageContent {
  slug: string; // Turkish slug, used at the root: /<slug>/
  enSlug: string; // English slug, used under /en/<enSlug>/
  tr: LandingLocale;
  en: LandingLocale;
}

export const landingPages: LandingPageContent[] = [
  {
    slug: "akaryakit-istasyonu-yazilimi",
    enSlug: "gas-station-software",
    tr: {
      metaTitle:
        "Akaryakıt İstasyonu Yazılımı | Mutabakat - Shift Tracker",
      metaDescription:
        "Akaryakıt istasyonu yazılımı: ERP/TURPAK satışlarını banka onaylı ödemelerle eşleştirin, ödeme tipi düzeltin, vardiya açıklarını ve nakit akışını yönetin.",
      h1: "Akaryakıt İstasyonu Yazılımı",
      heroSubtitle:
        "1,5-2 saat süren uzun vardiya mutabakatı ve açık tespiti sürecini otomatik hale getiren akaryakıt istasyonu yazılımı: vardiya alma işlemi 1 dakikanın altına iner, siz yalnızca sonuçları gözden geçirip kaydedersiniz.",
      intro:
        "Shift Tracker, akaryakıt istasyonları için tasarlanmış bir mutabakat ve nakit akışı yazılımıdır. TURPAK ve Asis gibi pompa otomasyon sistemlerinden gelen satış verisini banka hareketleriyle otomatik eşleştirir; kart ve nakit ödemelerin mutabakatını tamamlar ve kayıt altına alır. İstasyondaki her tutar uçtan uca takip edilir; size sadece sonuçları inceleyip onaylamak kalır.",
      featuresTitle: "Akaryakıt istasyonu yazılımında olması gerekenler",
      features: [
        {
          icon: "🔄",
          title: "Banka onaylı mutabakat",
          text: "ERP/otomasyon satışlarını banka kayıtlarıyla yan yana eşleştirin; uyumsuzluklar anında görünür hale gelir.",
        },
        {
          icon: "💳",
          title: "Ödeme tipi düzeltme",
          text: "Nakit girilmiş ama kartla ödenmiş satışları düzeltin; orijinal kayıt denetim için korunur.",
        },
        {
          icon: "🧾",
          title: "Tahsis bazlı inceleme",
          text: "Banka onaylı tutarları açıklayıcı nakit tutarlardan ayrı izleyin; her tutar izlenebilir kalır.",
        },
        {
          icon: "🔌",
          title: "TURPAK & Asis entegrasyonu",
          text: "Pompa otomasyonundan satış verisini alır; otomasyonun yapmadığı banka mutabakatını ekler.",
        },
        {
          icon: "🛡️",
          title: "Denetim izi ve suistimal önleme",
          text: "Her düzeltme gerekçe, inceleyen ve zaman damgasıyla kaydedilir; suistimal görünür olur.",
        },
        {
          icon: "⚡",
          title: "Saniyeler içinde vardiya",
          text: "Daha önce 1,5-2 saat süren manuel incelemeyi saniyelere indirin.",
        },
      ],
      guide: {
        title: "Akaryakıt istasyonu yazılımı rehberi",
        sections: [
          {
            heading: "Akaryakıt istasyonu yazılımı neden gerekir?",
            paragraphs: [
              "Bir akaryakıt istasyonunda her vardiyada yüzlerce kart ve nakit satış yapılır. Pompa otomasyonu (TURPAK, Asis) bu satışları kaydeder; ancak kartla ödendiği görünen bir satır, paranın bankaya geçtiğinin kanıtı değildir. POS gün sonu (Z) raporu ise gün sonunda kesilir ve vardiyalarla örtüşmez.",
              "Bu yüzden vardiya mutabakatı çoğu istasyonda elle yapılır, 1,5-2 saat sürer ve kasa açıkları ancak sonradan fark edilir. Akaryakıt istasyonu yazılımı, her satışı gerçek banka hareketiyle karşılaştırarak bu kontrolü her vardiyada otomatik yapar.",
            ],
          },
          {
            heading: "Shift Tracker adım adım nasıl çalışır?",
            paragraphs: [
              "Shift Tracker iki bağımsız kaynağı karşılaştırır: otomasyondaki satış (satış niyeti) ve bankanın POS hareketi (gerçek para hareketi).",
            ],
            steps: [
              "Satışlar pompa otomasyonundan (TURPAK, Asis) otomatik aktarılır.",
              "Bankaların gün sonu dosyaları gelir. Bir günün mutabakatı, o güne ait tüm aktif bankaların dosyaları gelene kadar bekletilir; eksik resimle eşleştirme yapılmaz.",
              "Kart satışları doğru banka hareketiyle eşleştirilir: aynı tutar, aynı terminal ve dar bir zaman aralığı. Bir satış yalnızca bir hareketle eşleşir, aynı ödeme iki kez sayılamaz.",
              "Nakit yatırmalar doğrulanır: her yatırmaya dekonta yazılan benzersiz bir referans verilir, sistem bu referansı ve tutarı banka ekstresinde arar.",
              "Her satır üç durumdan birinde görünür: eşleşti, inceleme gerekiyor veya açık. Açıklar ilgili vardiyaya ve personele atanır.",
              "Siz yalnızca rakamları gözden geçirip vardiyayı kilitlersiniz; vardiya alma bir dakikanın altına iner.",
            ],
            link: {
              to: "/blog/shift-tracker-mutabakati-nasil-calisir/",
              label: "Ayrıntılar: Shift Tracker mutabakatı nasıl çalışır?",
            },
          },
          {
            heading: "Otomasyon yazılımı ile mutabakat yazılımı arasındaki fark",
            paragraphs: [
              "TURPAK veya Asis gibi pompa otomasyonları satışı kaydeder ve raporlar; banka tarafını kontrol etmez. Shift Tracker otomasyonun yerini almaz, onun verisini kullanarak eksik olan banka mutabakatını ekler.",
              "Nakit girilmiş ama kartla ödenmiş satışlar da düzeltilebilir. Her düzeltmede orijinal ödeme tipi, yeni ödeme tipi, gerekçe, kullanıcı ve zaman saklanır; böylece kart satışını nakit gösterme gibi suistimaller görünür hale gelir.",
            ],
            link: {
              to: "/fraud-prevention/",
              label: "Akaryakıt istasyonunda suistimal önleme",
            },
          },
          {
            heading: "Kimler için uygundur?",
            paragraphs: [
              "Akaryakıt ve benzin istasyonu sahipleri, istasyon müdürleri ve muhasebe ekipleri için tasarlandı. Web tabanlıdır; tek istasyondan çok istasyonlu yapılara kadar rol bazlı erişimle kullanılır.",
              "Mutabakatın yanında açık vardiya için canlı satış ekranı, tank stok takibi, akıllı akaryakıt sipariş önerileri ve personel açık takibi de aynı panelde yer alır.",
            ],
          },
          {
            heading: "Fiyat ve paket içeriği",
            paragraphs: [
              "Tek plan, her şey dahil: TURPAK entegrasyonu, kart satışlarının banka ödemeleriyle eşleştirilmesi, nakit yatırmaların banka ekstresiyle doğrulanması, denetime hazır vardiya kayıtları ve doğrudan WhatsApp ve telefon desteği.",
              `Sınırlı süreli indirimle aylık ${PRICE_TEXT.tr.monthly} veya yıllık ${PRICE_TEXT.tr.annual} (KDV dahil). Aylık planda taahhüt yoktur ve ilk ${PRICE_TEXT.tr.moneyBackDays} gün koşulsuz para iade garantisi vardır.`,
            ],
            link: {
              to: "/fiyatlandirma/",
              label: "Fiyatlandırma ayrıntıları",
            },
          },
          {
            heading: "Neden Shift Tracker?",
            paragraphs: [
              "Shift Tracker'ı kendi akaryakıt istasyonumuz için geliştirdik ve her gün orada kullanıyoruz. Amaç, istasyonu güvene değil kanıta dayalı yönetmek: mutabakat bittiğinde istasyondaki her tutar gerçek bir banka işlemine dayanır.",
              "15 dakikalık bir demoda vardiya akışınızı birlikte inceliyor, mutabakatın kendi banka ve TURPAK verilerinizle nasıl çalıştığını gösteriyoruz.",
            ],
          },
        ],
      },
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Akaryakıt istasyonu yazılımı ne işe yarar?",
          answer:
            "Satış, mutabakat, nakit akışı ve vardiya süreçlerini tek yerden yönetir. Shift Tracker özellikle ERP/otomasyon satışlarını banka onaylı ödemelerle eşleştirerek mutabakat ve denetim sağlar.",
        },
        {
          question: "TURPAK veya Asis ile çalışır mı?",
          answer:
            "Evet. Pompa otomasyon sistemlerinden satış verisini alır ve banka onaylı kayıtlarla mutabık kılar.",
        },
        {
          question: "Vardiya açığını nasıl bulur?",
          answer:
            "Her vardiya banka onaylı ödemeler ve ERP toplamlarıyla mutabık kılınır; farklar ilgili vardiyaya ve personele atanır.",
        },
        {
          question: "Kaç istasyon için uygundur?",
          answer:
            "Tek istasyondan çok istasyonlu yapılara kadar, rol bazlı erişimle kullanılabilir.",
        },
        {
          question: "Akaryakıt istasyonu yazılımının fiyatı nedir?",
          answer: `Shift Tracker tek plan ve her şey dahil sunulur: sınırlı süreli indirimle aylık ${PRICE_TEXT.tr.monthly} veya yıllık ${PRICE_TEXT.tr.annual}, KDV dahil. İlk ${PRICE_TEXT.tr.moneyBackDays} gün koşulsuz para iade garantisi vardır.`,
        },
        {
          question: "Nakit yatırmalar nasıl doğrulanır?",
          answer: "Her nakit yatırmaya dekonta yazılan benzersiz bir referans verilir. Sistem banka ekstresinde bu referansı ve tutarı arar; eksik, mükerrer veya kayıp yatırmaları işaretler.",
        },
        {
          question: "Demo nasıl alabilirim?",
          answer: "İletişim formundan, WhatsApp veya telefonla (+90 532 552 09 03) bize ulaşın. 15 dakikalık demoda mutabakatı kendi banka ve TURPAK verilerinizle gösteriyoruz.",
        },
      ],
      ctaTitle: "Akaryakıt istasyonunuz için demo alın",
      ctaText:
        "Mutabakat, ödeme düzeltme ve nakit akışını istasyonlarınızda birlikte inceleyelim. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Akaryakıt İstasyonu Yazılımı",
    },
    en: {
      metaTitle:
        "Gas Station Software | Reconciliation - Shift Tracker",
      metaDescription:
        "Gas station software that matches ERP/TURPAK sales with bank-confirmed payments, corrects payment types, and manages shift deficits and cash flow.",
      h1: "Gas Station Software",
      heroSubtitle:
        "Gas station software that automates the 1.5-2 hour shift reconciliation and deficit-detection process, bringing shift calculation under one minute, so you just review the results and save.",
      intro:
        "Shift Tracker is reconciliation and cash-flow software built for fuel and gas stations. It automatically matches sales data from pump automation systems like TURPAK and Asis against bank movements, completing and recording the reconciliation of both card and cash payments. Every amount at the station is tracked end to end; all that is left for you is to review and approve the results.",
      featuresTitle: "What gas station software should do",
      features: [
        {
          icon: "🔄",
          title: "Bank-confirmed reconciliation",
          text: "Match ERP/automation sales against bank records side by side; mismatches surface immediately.",
        },
        {
          icon: "💳",
          title: "Payment type correction",
          text: "Fix sales entered as cash that were paid by card; the original entry is preserved for audit.",
        },
        {
          icon: "🧾",
          title: "Allocation-aware review",
          text: "Track bank-confirmed amounts separately from descriptive cash amounts; every amount stays traceable.",
        },
        {
          icon: "🔌",
          title: "TURPAK & Asis integration",
          text: "Pulls sales data from pump automation and adds the bank reconciliation automation doesn't do.",
        },
        {
          icon: "🛡️",
          title: "Audit trail & fraud prevention",
          text: "Every correction is recorded with its reason, reviewer and timestamp, so fraud becomes visible.",
        },
        {
          icon: "⚡",
          title: "Shifts in seconds",
          text: "Cut a review that used to take 1.5-2 hours down to seconds.",
        },
      ],
      guide: {
        title: "Gas station software guide",
        sections: [
          {
            heading: "Why does a gas station need dedicated software?",
            paragraphs: [
              "A fuel station records hundreds of card and cash sales every shift. Pump automation (TURPAK, Asis) records those sales, but a row that says it was paid by card is not proof that the money reached the bank. The POS end-of-day (Z) report is cut at day's end and does not line up with shifts.",
              "That is why shift reconciliation is usually done by hand, takes 1.5 to 2 hours, and cash deficits surface only later. Gas station software runs this check automatically on every shift by comparing each sale with the real bank transaction.",
            ],
          },
          {
            heading: "How Shift Tracker works, step by step",
            paragraphs: [
              "Shift Tracker compares two independent sources: the sale in your automation (the sale intent) and the bank's POS transaction (the real money movement).",
            ],
            steps: [
              "Sales are imported automatically from your pump automation (TURPAK, Asis).",
              "The banks' end-of-day files arrive. A day's reconciliation is held until every active bank's file for that day has landed; it never matches against an incomplete picture.",
              "Card sales are matched to the right bank transaction: the same amount, the same terminal and a narrow time window. A sale can match only one transaction, so the same payment can never be counted twice.",
              "Cash deposits are verified: each deposit gets a unique reference written on the deposit slip, and the system looks for that reference and amount on your bank statement.",
              "Every row shows one of three states: matched, needs review or deficit. Deficits are attributed to the relevant shift and employee.",
              "You only review the figures and lock the shift; closing a shift takes under a minute.",
            ],
            link: {
              to: "/en/blog/how-shift-tracker-reconciliation-works/",
              label: "In depth: how Shift Tracker reconciliation works",
            },
          },
          {
            heading: "Automation software vs reconciliation software",
            paragraphs: [
              "Pump automation such as TURPAK or Asis records and reports sales; it does not check the bank side. Shift Tracker does not replace your automation: it uses its data and adds the missing bank reconciliation.",
              "Sales entered as cash but paid by card can be corrected. Each correction keeps the original payment type, the new type, the reason, the user and the time, so abuse such as recording card sales as cash becomes visible.",
            ],
            link: {
              to: "/en/fraud-prevention/",
              label: "Fraud prevention at fuel stations",
            },
          },
          {
            heading: "Who is it for?",
            paragraphs: [
              "Built for fuel and gas station owners, station managers and accounting teams. It is web-based and works from a single station to multi-station operations with role-based access.",
              "Next to reconciliation, the same panel includes a live sales screen for the open shift, tank stock tracking, smart fuel order suggestions and employee deficit tracking.",
            ],
          },
          {
            heading: "Pricing and what's included",
            paragraphs: [
              "One plan, everything included: TURPAK integration, matching card sales with real bank payments, verifying cash deposits against your bank statement, audit-ready shift records and direct WhatsApp and phone support.",
              `${PRICE_TEXT.eng.monthly} per month or ${PRICE_TEXT.eng.annual} per year with a limited-time discount (VAT included). The monthly plan has no commitment, and the first ${PRICE_TEXT.eng.moneyBackDays} days come with an unconditional money-back guarantee.`,
            ],
            link: {
              to: "/en/pricing/",
              label: "Pricing details",
            },
          },
          {
            heading: "Why Shift Tracker?",
            paragraphs: [
              "We built Shift Tracker for our own fuel station and run it there every day. The goal is station management based on proof, not trust: when reconciliation is done, every amount at the station traces back to a real bank transaction.",
              "In a 15-minute demo we walk through your shift flow and show how reconciliation works with your own bank and TURPAK data.",
            ],
          },
        ],
      },
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "What does gas station software do?",
          answer:
            "It manages sales, reconciliation, cash flow and shifts in one place. Shift Tracker specifically matches ERP/automation sales against bank-confirmed payments for reconciliation and audit.",
        },
        {
          question: "Does it work with TURPAK or Asis?",
          answer:
            "Yes. It pulls sales data from pump automation systems and reconciles it against bank-confirmed records.",
        },
        {
          question: "How does it find shift deficits?",
          answer:
            "Each shift is reconciled against bank-confirmed payments and ERP totals; differences are attributed to the relevant shift and employee.",
        },
        {
          question: "How many stations does it support?",
          answer:
            "From a single station to multi-station operations, with role-based access.",
        },
        {
          question: "How much does gas station software cost?",
          answer: `Shift Tracker is one plan with everything included: ${PRICE_TEXT.eng.monthly} per month or ${PRICE_TEXT.eng.annual} per year with a limited-time discount, VAT included, with a ${PRICE_TEXT.eng.moneyBackDays}-day money-back guarantee.`,
        },
        {
          question: "How are cash deposits verified?",
          answer: "Each cash deposit gets a unique reference written on the deposit slip. The system looks for that reference and amount on your bank statement and flags missing, duplicate or lost deposits.",
        },
        {
          question: "How do I get a demo?",
          answer: "Reach us through the contact form, WhatsApp or phone (+90 532 552 09 03). In a 15-minute demo we show reconciliation with your own bank and TURPAK data.",
        },
      ],
      ctaTitle: "Get a demo for your gas station",
      ctaText:
        "Let's walk through reconciliation, payment correction and cash flow for your stations. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Gas Station Software",
    },
  },
  {
    slug: "benzin-istasyonu-yazilimi",
    enSlug: "fuel-station-software",
    tr: {
      metaTitle:
        "Benzin İstasyonu Yazılımı ve Programı - Shift Tracker",
      metaDescription:
        "Benzin istasyonu yazılımı/programı: vardiya kapanışı, nakit ve kart kontrolü, personel açığı takibi ve banka mutabakatını saniyeler içinde yapın.",
      h1: "Benzin İstasyonu Yazılımı",
      heroSubtitle:
        "Benzin istasyonu sahipleri için: 1,5-2 saat süren vardiya alma sürecini otomatiğe alıyoruz. Çoğu vardiya otomatik eşleştirilir; siz yalnızca personel açıklarını gözden geçirip kaydedersiniz, tam bir vardiya bir dakikanın altında, tam şeffaflıkla.",
      intro:
        "Shift Tracker, benzin istasyonu işletmecileri için günlük vardiya kontrolünü basitleştiren bir yazılımdır. Kredi kartı satışları banka onaylı kayıtlardan otomatik eşleştirilip onaylanır; bütün nakit ödemeler ise bankaya yatan tutar üzerinden teyit edilir ve sistemdeki bütün açıklar kapanır. Böylece her vardiyayı saatler yerine saniyeler içinde alırsınız.",
      featuresTitle: "Benzin istasyonu programında öne çıkanlar",
      features: [
        {
          icon: "⚡",
          title: "Hızlı vardiya kapanışı",
          text: "1,5-2 saatlik incelemeyi saniyelere indirin; bazı vardiyalar hiç manuel adım gerektirmez.",
        },
        {
          icon: "💳",
          title: "Otomatik kart mutabakatı",
          text: "Kredi kartı satışları banka onaylı kayıtlardan otomatik eşleştirilip onaylanır.",
        },
        {
          icon: "💰",
          title: "Nakit kontrolü",
          text: "Yalnızca bankaya yatan nakdi doğrulayın; nakit açığı erkenden görünür olur.",
        },
        {
          icon: "🧑‍🔧",
          title: "Personel açığı takibi",
          text: "Farklar gerekçesiyle birlikte ilgili vardiyaya ve personele atanır.",
        },
        {
          icon: "🛡️",
          title: "Suistimal önleme",
          text: "Kart satışını nakit gösterme gibi hileler banka mutabakatıyla engellenir.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Benzin istasyonu programı nedir?",
          answer:
            "Vardiya, nakit, kart ve personel açığı gibi günlük operasyonu yöneten yazılımdır. Shift Tracker bunları banka onaylı mutabakatla birleştirir.",
        },
        {
          question: "Kurulum gerektirir mi?",
          answer:
            "Hayır, web tabanlıdır; herhangi bir tarayıcıdan erişilir, kurulum gerektirmez.",
        },
        {
          question: "Vardiya kapanışı ne kadar sürer?",
          answer:
            "Otomatik kart mutabakatı sayesinde çoğu vardiya saniyeler içinde, en fazla birkaç dakikada kapanır.",
        },
        {
          question: "Birden fazla benzin istasyonu için uygun mu?",
          answer:
            "Evet, çok istasyonlu yapıları rol bazlı erişimle destekler.",
        },
      ],
      ctaTitle: "Benzin istasyonunuz için demo alın",
      ctaText:
        "Vardiya, nakit ve kart kontrolünü kendi istasyonunuzda görün. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Benzin İstasyonu Yazılımı",
    },
    en: {
      metaTitle:
        "Fuel Station Software | Shift & Cash Control - Shift Tracker",
      metaDescription:
        "Fuel station software/program to close shifts fast, control cash and card, track employee deficits, and reconcile against the bank in seconds.",
      h1: "Fuel Station Software",
      heroSubtitle:
        "For fuel station owners: we automate the 1.5-2 hour shift calculation process. Most shifts auto-match, so you just review employee deficits and save, a full shift in under a minute, with full transparency.",
      intro:
        "Shift Tracker simplifies daily shift control for fuel station operators. Credit card sales are matched and approved automatically from bank-confirmed records, and every cash payment is confirmed against the amount deposited to the bank, closing every deficit in the system. So you take each shift in seconds instead of hours.",
      featuresTitle: "What stands out in the fuel station program",
      features: [
        {
          icon: "⚡",
          title: "Fast shift close",
          text: "Cut a 1.5-2 hour review to seconds; some shifts need no manual steps at all.",
        },
        {
          icon: "💳",
          title: "Automatic card reconciliation",
          text: "Credit card sales are matched and approved automatically from bank-confirmed records.",
        },
        {
          icon: "💰",
          title: "Cash control",
          text: "Verify only the cash deposited to the bank; cash deficits surface early.",
        },
        {
          icon: "🧑‍🔧",
          title: "Employee deficit tracking",
          text: "Differences are attributed, with their reason, to the relevant shift and employee.",
        },
        {
          icon: "🛡️",
          title: "Fraud prevention",
          text: "Tricks like recording a card sale as cash are blocked by bank reconciliation.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "What is fuel station software?",
          answer:
            "It manages daily operations like shifts, cash, card and employee deficits. Shift Tracker ties these together with bank-confirmed reconciliation.",
        },
        {
          question: "Does it need installation?",
          answer:
            "No, it is web-based and accessed from any browser, no installation required.",
        },
        {
          question: "How long does a shift close take?",
          answer:
            "Thanks to automatic card reconciliation, most shifts close in seconds and a few minutes at most.",
        },
        {
          question: "Is it suitable for multiple stations?",
          answer:
            "Yes, it supports multi-station operations with role-based access.",
        },
      ],
      ctaTitle: "Get a demo for your fuel station",
      ctaText:
        "See shift, cash and card control on your own station. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Fuel Station Software",
    },
  },
  {
    slug: "vardiya-alma-programi",
    enSlug: "shift-management-program",
    tr: {
      metaTitle:
        "Vardiya Alma Programı | Akaryakıt İstasyonu - Shift Tracker",
      metaDescription:
        "Vardiya alma ve devir programı: akaryakıt istasyonunda vardiya kapanışını, nakit ve kart kontrolünü, personel açığını saniyeler içinde yönetin.",
      h1: "Vardiya Alma Programı",
      heroSubtitle:
        "1,5-2 saat süren uzun vardiya alma ve açık tespiti sürecini otomatikleştiren program: çoğu vardiya hazır gelir, siz yalnızca açıkları gözden geçirip kaydedersiniz, vardiya alma bir dakikanın altında, tam denetim iziyle.",
      intro:
        "Shift Tracker, akaryakıt istasyonlarında vardiya alma ve devir sürecini basitleştiren bir programdır. Her vardiya; nakit, kredi kartı ve veresiye satışlarıyla birlikte banka onaylı kayıtlarla mutabık kılınır. Siz istasyonunuzun operasyonlarını mükemmelleştirmeye odaklanırken, Shift Tracker arka planda tüm nakit akışını otomatik kontrol eder; böylece vardiyayı devralan personel, devraldığı tutarın doğruluğundan emin olur.",
      featuresTitle: "Vardiya alma programında olması gerekenler",
      features: [
        {
          icon: "⚡",
          title: "Hızlı vardiya kapanışı",
          text: "1,5-2 saatlik işi saniyelere indirin; bazı vardiyalar manuel adım gerektirmez.",
        },
        {
          icon: "🔁",
          title: "Net vardiya devri",
          text: "Bir vardiyadan diğerine mutabık, açık ve izlenebilir devir.",
        },
        {
          icon: "💳",
          title: "Kart ve nakit kontrolü",
          text: "Satışları banka onaylı kayıtlarla eşleştirin; uyumsuzluk anında görünür.",
        },
        {
          icon: "🧑‍🔧",
          title: "Personel açığı takibi",
          text: "Farklar gerekçesiyle ilgili vardiyaya ve personele atanır.",
        },
        {
          icon: "🛡️",
          title: "Denetim izi",
          text: "Her düzeltme gerekçe, inceleyen ve zaman damgasıyla kaydedilir.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Vardiya alma programı nedir?",
          answer:
            "Vardiya başlangıcı ve kapanışında satış, nakit, kart ve açıkları kayıt altına alıp bir sonraki vardiyaya devreden yazılımdır. Shift Tracker bunu banka onaylı mutabakatla yapar.",
        },
        {
          question: "Vardiya kapanışı ne kadar sürer?",
          answer:
            "Otomatik kart mutabakatı sayesinde çoğu vardiya saniyeler içinde, en fazla birkaç dakikada kapanır.",
        },
        {
          question: "Personel açığı nasıl belirlenir?",
          answer:
            "Her vardiya banka onaylı ödemeler ve satış toplamlarıyla mutabık kılınır; fark ilgili personele atanır.",
        },
        {
          question: "TURPAK veya Asis ile çalışır mı?",
          answer:
            "Evet. Pompa otomasyonundan satış verisini alır ve banka onaylı kayıtlarla mutabık kılar.",
        },
      ],
      ctaTitle: "İstasyonunuz için demo alın",
      ctaText:
        "Vardiya alma ve kapanış akışını kendi istasyonunuzda görün. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Vardiya Alma Programı",
    },
    en: {
      metaTitle:
        "Shift Management Program for Fuel Stations - Shift Tracker",
      metaDescription:
        "Shift handover and management program: close shifts, control cash and card, and track employee deficits at a fuel station in seconds.",
      h1: "Shift Management Program",
      heroSubtitle:
        "A program that automates the 1.5-2 hour shift calculation and deficit-detection process: most shifts arrive ready, you just review deficits and save, in under a minute, with a complete audit trail.",
      intro:
        "Shift Tracker simplifies shift calculation and handover at fuel stations. Each shift, with its cash, card and credit sales, is reconciled against bank-confirmed records. While you focus on perfecting your station's operations, Shift Tracker controls all the cash flow in the background, so the incoming attendant can trust the figures they are taking over.",
      featuresTitle: "What a shift management program should do",
      features: [
        {
          icon: "⚡",
          title: "Fast shift close",
          text: "Cut a 1.5-2 hour task to seconds; some shifts need no manual steps.",
        },
        {
          icon: "🔁",
          title: "Clean handover",
          text: "A reconciled, clear and traceable handover from one shift to the next.",
        },
        {
          icon: "💳",
          title: "Cash & card control",
          text: "Match sales against bank-confirmed records; mismatches surface immediately.",
        },
        {
          icon: "🧑‍🔧",
          title: "Employee deficit tracking",
          text: "Differences are attributed, with reason, to the relevant shift and employee.",
        },
        {
          icon: "🛡️",
          title: "Audit trail",
          text: "Every correction is recorded with its reason, reviewer and timestamp.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "What is a shift management program?",
          answer:
            "Software that records sales, cash, card and deficits at shift start and close, then hands them to the next shift. Shift Tracker does this with bank-confirmed reconciliation.",
        },
        {
          question: "How long does a shift close take?",
          answer:
            "Thanks to automatic card reconciliation, most shifts close in seconds and a few minutes at most.",
        },
        {
          question: "How are employee deficits determined?",
          answer:
            "Each shift is reconciled against bank-confirmed payments and sales totals; the difference is attributed to the relevant employee.",
        },
        {
          question: "Does it work with TURPAK or Asis?",
          answer:
            "Yes. It pulls sales data from pump automation and reconciles it against bank-confirmed records.",
        },
      ],
      ctaTitle: "Get a demo for your station",
      ctaText:
        "See the shift handover and close flow on your own station. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Shift Management Program",
    },
  },
  {
    slug: "akaryakit-istasyonu-muhasebe-programi",
    enSlug: "gas-station-accounting-reconciliation",
    tr: {
      metaTitle:
        "Akaryakıt İstasyonu Muhasebe Programı - Shift Tracker",
      metaDescription:
        "Akaryakıt istasyonu muhasebe programınız için banka onaylı mutabakat: kart ve nakit satışları bankayla eşleştirin, muhasebeye doğru veri akıtın.",
      h1: "Akaryakıt İstasyonu Muhasebe Programı ve Mutabakat",
      heroSubtitle:
        "Muhasebe programınıza yalnızca banka onaylı, mutabık veri akıtan otomatik mutabakat katmanı: finansal süreçler hızlanır, kontroller standartlaşır ve tam denetlenebilirlik sağlanır.",
      intro:
        "Shift Tracker tam bir muhasebe (e-fatura, e-defter, genel muhasebe) programı değildir; muhasebenizin doğru veriyle çalışmasını sağlayan banka onaylı mutabakat katmanıdır. Kart ve nakit satışları banka kayıtlarıyla eşleştirir, ödeme tiplerini düzeltir ve muhasebe programınıza güvenilir, mutabık veri akıtır.",
      featuresTitle: "Muhasebe öncesi mutabakatta öne çıkanlar",
      features: [
        {
          icon: "🔄",
          title: "Banka onaylı mutabakat",
          text: "Satışları banka kayıtlarıyla eşleştirin; defterlerinize yalnızca doğrulanmış tutar gider.",
        },
        {
          icon: "💳",
          title: "Ödeme tipi düzeltme",
          text: "Nakit/kart karışıklığını düzeltin; muhasebeye yanlış sınıflandırma gitmez.",
        },
        {
          icon: "🧾",
          title: "Tahsis bazlı inceleme",
          text: "Banka onaylı tutarları açıklayıcı nakit tutarlardan ayırın.",
        },
        {
          icon: "🔌",
          title: "ERP ve muhasebe uyumu",
          text: "TURPAK/Asis otomasyonu ve muhasebe programlarınızla birlikte çalışır.",
        },
        {
          icon: "🛡️",
          title: "Denetim izi",
          text: "Her düzeltme gerekçe ve zaman damgasıyla kaydedilir.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Akaryakıt istasyonu muhasebe programının yerine geçer mi?",
          answer:
            "Hayır. Shift Tracker bir muhasebe programı değil, muhasebe öncesi banka onaylı mutabakat katmanıdır; mevcut muhasebe programınızla birlikte çalışır.",
        },
        {
          question: "Muhasebeye hangi veriyi sağlar?",
          answer:
            "Banka onaylı ve mutabık satış/tahsilat verisi sağlar; böylece defterleriniz doğru tutara dayanır.",
        },
        {
          question: "ERP veya otomasyonla çalışır mı?",
          answer:
            "Evet. TURPAK ve Asis gibi sistemlerden satış verisini alır, banka ile mutabık kılar.",
        },
        {
          question: "Neden ayrı bir mutabakat katmanı gerekir?",
          answer:
            "Muhasebe programları bir satışın gerçekten bankaya geçtiğini doğrulamaz; Shift Tracker bunu yapar.",
        },
      ],
      ctaTitle: "Muhasebenizi banka onaylı veriyle besleyin",
      ctaText:
        "Mutabakat katmanını mevcut muhasebe programınızla birlikte görün. Shift Tracker ile finansal süreçler hızlanır, kontroller standartlaşır ve istasyonunuzun nakit akışı her an güvence altında olur.",
      ctaButton: "Demo talep edin",
      serviceName: "Muhasebe Programı Mutabakatı",
    },
    en: {
      metaTitle:
        "Gas Station Accounting Reconciliation - Shift Tracker",
      metaDescription:
        "Bank-confirmed reconciliation for your gas station accounting software: match card and cash sales to the bank before they hit the books.",
      h1: "Gas Station Accounting Reconciliation",
      heroSubtitle:
        "An automated reconciliation layer that feeds your accounting software only bank-confirmed, reconciled data: financial processes speed up, controls become standardized, and everything stays fully auditable.",
      intro:
        "Shift Tracker is not a full accounting suite (e-invoice, ledger); it is the bank-confirmed reconciliation layer that keeps your accounting accurate. It matches card and cash sales against bank records, corrects payment types, and feeds reliable, reconciled data into your accounting program.",
      featuresTitle: "What stands out in pre-accounting reconciliation",
      features: [
        {
          icon: "🔄",
          title: "Bank-confirmed reconciliation",
          text: "Match sales against bank records so only verified amounts reach your books.",
        },
        {
          icon: "💳",
          title: "Payment type correction",
          text: "Fix cash/card mix-ups so no misclassification reaches accounting.",
        },
        {
          icon: "🧾",
          title: "Allocation-aware review",
          text: "Keep bank-confirmed amounts separate from descriptive cash amounts.",
        },
        {
          icon: "🔌",
          title: "ERP & accounting fit",
          text: "Works alongside TURPAK/Asis automation and your accounting program.",
        },
        {
          icon: "🛡️",
          title: "Audit trail",
          text: "Every correction is recorded with its reason and timestamp.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "Does it replace gas station accounting software?",
          answer:
            "No. Shift Tracker is not an accounting suite; it is the bank-confirmed reconciliation layer that works alongside your existing accounting software.",
        },
        {
          question: "What data does it feed into accounting?",
          answer:
            "Bank-confirmed, reconciled sales and collection data, so your books rest on accurate figures.",
        },
        {
          question: "Does it work with ERP or automation?",
          answer:
            "Yes. It pulls sales data from systems like TURPAK and Asis and reconciles it against the bank.",
        },
        {
          question: "Why is a separate reconciliation layer needed?",
          answer:
            "Accounting programs don't verify that a sale actually reached the bank; Shift Tracker does.",
        },
      ],
      ctaTitle: "Feed your accounting with bank-proven data",
      ctaText:
        "See the reconciliation layer working alongside your accounting program. With Shift Tracker, financial processes speed up, controls become standardized, and your station's cash flow stays secured at all times.",
      ctaButton: "Request a demo",
      serviceName: "Accounting Reconciliation",
    },
  },
  {
    slug: "nakit-yatirma-dogrulama",
    enSlug: "deposit-verification",
    tr: {
      metaTitle:
        "Nakit Yatırma Doğrulama | Banka Onaylı Kasa - Shift Tracker",
      metaDescription:
        "Her nakit yatırma gerçek banka ekstrenizle eşleştirilip aynı gün otomatik doğrulanır; eksik, mükerrer veya hiç yapılmamış yatırmalar işaretlenir.",
      h1: "Otomatik Nakit Yatırma Doğrulama",
      heroSubtitle:
        "Bütün nakit ödemeler bankaya yatan tutar üzerinden teyit edilir ve sistemdeki bütün açıklar kapanır. Her nakit yatırma, gerçek banka ekstrenizle eşleştirilip aynı gün doğrulanır.",
      intro:
        "Kasa ile banka hesabı arasında nakit eksik yatırılabilir, iki kez yatırılabilir ya da hiç yatırılmayabilir ve sahipler bunu çoğu zaman ay sonunda, haftalar sonra fark eder. Shift Tracker bu açığı kapatır: benzersiz bir referans, uygulamadaki her yatırmayı gerçek bir banka işlemine bağlar ve banka ekstreniz doğruluğun kaynağı olur.",
      featuresTitle: "Banka onaylı yatırmalar nasıl çalışır",
      features: [
        {
          icon: "🔑",
          title: "Her yatırma için benzersiz referans",
          text: "Nakit yatırıldı olarak işaretlendiğinde uygulama, beklenen tutar ve banka günüyle birlikte okunması kolay, benzersiz bir kod (örn. C4S1D06062601) üretir.",
        },
        {
          icon: "🏦",
          title: "Banka ekstrenizle eşleştirilir",
          text: "Shift Tracker, güvenli bir API ile banka hesabınızı izler ve bu referansı ve tam tutarı taşıyan bir para girişi işlemi arar.",
        },
        {
          icon: "✅",
          title: "Otomatik, aynı gün onay",
          text: "Güvenli bir eşleşmede yatırma kendiliğinden doğrulanır, genellikle aynı iş günü içinde dakikalar ila birkaç saatte.",
        },
        {
          icon: "🚩",
          title: "Eksik, mükerrer veya kayıp: işaretlenir",
          text: "Tutar uyuşmazlıkları, mükerrer kayıtlar ve gün sonuna kadar gelmeyen yatırmalar, aynı gün incelenmek üzere uyarı oluşturur.",
        },
        {
          icon: "📄",
          title: "Referans; slip, SMS ve e-postada",
          text: "Yatırmayı yapan kişi kodu her zaman elinin altında bulur: yazdırılabilir yatırma slibinde, SMS ve e-posta ile.",
        },
        {
          icon: "🔒",
          title: "Tasarımdan gelen gizlilik",
          text: "Banka kimlik bilgileri şifrelenir, her şirket tamamen izole edilir ve banka ekstreniz saklanmaz: yalnızca her yatırmanın sonucu tutulur.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Bir yatırma nasıl doğrulanır?",
          answer:
            "Ekibiniz nakdi yatırıldı olarak işaretler ve bankada yazılacak benzersiz bir referans alır. Shift Tracker banka hesabınızı izler ve bu referansı ve tam tutarı taşıyan bir para girişi işlemi göründüğü anda otomatik doğrular.",
        },
        {
          question: "Anında mı oluyor?",
          answer:
            "Hayır: otomatik ve aynı gün. Önce bankanın işlemi işlemesi gerekir; yaklaşık 30 dakikada bir yapılan kontrol bunu yakalar, genellikle aynı iş günü dakikalar ila birkaç saat içinde. Hafta sonu veya tatil yatırmaları bir sonraki iş günü doğrulanır.",
        },
        {
          question: "Referans bankada yazılmazsa ne olur?",
          answer:
            "Unutulur veya yanlış yazılırsa yatırma otomatik eşleşmez: inceleme için raporlanır ve elle doğrularsınız. Hiçbir şey kaybolmaz; yalnızca otomatik onaylanmaz.",
        },
        {
          question: "Kontrol bende mi kalır?",
          answer:
            "Evet. Yöneticiler bir yatırmayı her zaman elle doğrulayabilir veya doğrulamayı kaldırabilir ve bu karar nihaidir. Otomasyon yalnızca yardımcı olur.",
        },
        {
          question: "Hangi bankalar destekleniyor?",
          answer:
            "İlk entegrasyon VakıfBank'tır ve tasarım, zamanla başka bankaların eklenmesini destekler.",
        },
      ],
      ctaTitle: "Kasa döngüsünü otomatik kapatın",
      ctaText:
        "Banka onaylı nakit yatırmaların uçtan uca vardiya doğrulamanızı nasıl tamamladığını görün. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Nakit Yatırma Doğrulama",
    },
    en: {
      metaTitle:
        "Deposit Verification | Bank-Confirmed Cash - Shift Tracker",
      metaDescription:
        "Every cash deposit is matched against your real bank statement and confirmed automatically the same day; short, duplicate or missing deposits are flagged.",
      h1: "Automatic Deposit Verification",
      heroSubtitle:
        "Every cash payment is confirmed against the amount that reaches the bank, closing every deficit in the system. Each cash deposit is matched against your real bank statement and verified the same day.",
      intro:
        "Between the till and the bank account, cash can be deposited short, deposited twice, or never deposited at all, and owners usually only catch it weeks later at month-end. Shift Tracker closes that gap: a unique reference ties each in-app deposit to a real bank transaction, and your bank statement becomes the source of truth.",
      featuresTitle: "How bank-confirmed deposits work",
      features: [
        {
          icon: "🔑",
          title: "A unique reference per deposit",
          text: "When cash is marked deposited, the app issues a unique, dictation-friendly code (e.g. C4S1D06062601) with the expected amount and bank business day.",
        },
        {
          icon: "🏦",
          title: "Matched against your bank statement",
          text: "Shift Tracker watches your bank account through a secure API and looks for a money-in transaction carrying that reference and the exact amount.",
        },
        {
          icon: "✅",
          title: "Automatic, same-day confirmation",
          text: "On a confident match the deposit flips to verified on its own, usually within minutes to a few hours, the same business day.",
        },
        {
          icon: "🚩",
          title: "Short, duplicate or missing: flagged",
          text: "Amount mismatches, duplicates, and deposits that never arrive by end of day raise a same-day alert to investigate.",
        },
        {
          icon: "📄",
          title: "Reference on slip, SMS and email",
          text: "The depositor always has the code to hand: on the printable deposit slip, by SMS, and by email.",
        },
        {
          icon: "🔒",
          title: "Private by design",
          text: "Bank credentials are encrypted, each company is fully isolated, and your bank statement is never stored: only each deposit's result.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "How does a deposit get verified?",
          answer:
            "Your team marks the cash deposited and gets a unique reference to write at the bank. Shift Tracker then watches your bank account and auto-confirms the moment a money-in transaction with that reference and the exact amount appears.",
        },
        {
          question: "Is it instant?",
          answer:
            "No: it's automatic and same-day. The bank has to post the transaction first; the next check (about every 30 minutes) picks it up, typically within minutes to a few hours on the same business day. Weekend or holiday deposits verify on the next business day.",
        },
        {
          question: "What if the reference isn't written at the bank?",
          answer:
            "If it's forgotten or mistyped the deposit won't auto-match: it's reported for review and you verify it by hand. Nothing is lost; it just isn't auto-confirmed.",
        },
        {
          question: "Do I stay in control?",
          answer:
            "Yes. Admins can always verify or unverify a deposit by hand, and that decision is final. The automation only assists.",
        },
        {
          question: "Which banks are supported?",
          answer:
            "VakıfBank is the first integration, and the design supports adding more banks over time.",
        },
      ],
      ctaTitle: "Close the cash loop, automatically",
      ctaText:
        "See how bank-confirmed cash deposits complete your end-to-end shift verification. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Deposit Verification",
    },
  },
  {
    slug: "kredi-karti-odeme-dogrulama",
    enSlug: "credit-card-payment-verification",
    tr: {
      metaTitle:
        "Kredi Kartı Ödeme Doğrulama | Banka Onaylı - Shift Tracker",
      metaDescription:
        "Kredi kartı satışlarını banka onaylı ödemelerle otomatik eşleştirin: bankaya geçenler kendiliğinden onaylanır, ulaşmayanlar işaretlenir.",
      h1: "Otomatik Kredi Kartı Ödeme Doğrulama",
      heroSubtitle:
        "Her kredi kartı satışının gerçekten bankanıza ulaştığını otomatik olarak bilin. Bütün ödemeler banka ile karşılaştırılıp teyit edilir; böylece POS slibi kaçması veya kaybolması derdi kalmaz.",
      intro:
        "Otomasyonda görünen bir kart satışı, paranın bankaya geçtiği anlamına gelmez. Shift Tracker bu açığı kapatır: her kart satışını bankanın onayladığı ödemelerle eşleştirir, geçenleri otomatik onaylar ve geçmeyenleri öne çıkarır, böylece nakit olarak girilen bir kart satışı, eksik bir tahsilat veya bir uyuşmazlık fark edilmeden geçemez.",
      featuresTitle: "Banka onaylı kart ödemeleri nasıl çalışır",
      features: [
        {
          icon: "🔄",
          title: "Banka ödemeleriyle eşleştirilir",
          text: "ERP/otomasyon (TURPAK, Asis) kart satışları, bankanın gerçekten onayladığı kart ödemeleriyle yan yana karşılaştırılır.",
        },
        {
          icon: "✅",
          title: "Otomatik onay",
          text: "Bankaya geçen kart satışları kendiliğinden eşleştirilip onaylanır; çoğu vardiya manuel adım gerektirmez.",
        },
        {
          icon: "🚩",
          title: "Geçmeyen veya uyuşmayan: işaretlenir",
          text: "Bankaya hiç ulaşmamış ya da tutarı tutmayan bir kart satışı, sessizce geçmek yerine inceleme için öne çıkarılır.",
        },
        {
          icon: "💳",
          title: "Ödeme tipi düzeltme",
          text: "Yanlışlıkla nakit girilmiş bir satış karta düzeltilerek doğrulama akışına alınır ve bankayla eşleşir.",
        },
        {
          icon: "🧾",
          title: "Tahsis bazlı kanıt",
          text: "Banka onaylı kart tutarları ayrı izlenir; her biri gerçek bir banka ödemesine, denetim kanıtı olarak bağlanır.",
        },
        {
          icon: "🔒",
          title: "Kontrol sizde",
          text: "Yöneticiler her zaman gözden geçirip düzenleyebilir; otomasyon yalnızca yardımcı olur ve tam denetim izi tutar.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Kart ödemeleri nasıl doğrulanır?",
          answer:
            "Shift Tracker, ERP/otomasyondan gelen kart satışlarını bankanın onayladığı kart ödemeleriyle karşılaştırır ve eşleşen banka ödemesine sahip her satışı otomatik onaylar.",
        },
        {
          question: "Neleri yakalar?",
          answer:
            "Bankaya hiç geçmemiş kart satışlarını, tutar uyuşmazlıklarını ve yanlış ödeme tipiyle girilmiş satışları: aynı vardiyada inceleme için öne çıkarır.",
        },
        {
          question: "TURPAK veya Asis ile çalışır mı?",
          answer:
            "Evet. Pompa otomasyonundan kart satışlarını alır ve banka onaylı ödemelerle mutabık kılar.",
        },
        {
          question: "Kontrol bende mi kalır?",
          answer:
            "Evet. Yöneticiler her eşleşmeyi elle gözden geçirip düzenleyebilir; otomasyon yalnızca yardımcı olur ve denetim izini korur.",
        },
        {
          question: "Bu, nakit yatırma doğrulamayla nasıl ilişkili?",
          answer:
            "Birlikte uçtan uca banka onaylı vardiyalar sunarlar: kart satışları banka ödemeleriyle, nakit yatırmalar banka ekstrenizle doğrulanır.",
        },
      ],
      ctaTitle: "Her kart satışını bankayla doğrulayın",
      ctaText:
        "Banka onaylı kart ödemelerinin uçtan uca vardiya doğrulamanızı nasıl tamamladığını görün. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Kredi Kartı Ödeme Doğrulama",
    },
    en: {
      metaTitle:
        "Credit Card Payment Verification - Shift Tracker",
      metaDescription:
        "Match credit card sales to bank-confirmed payments automatically: settled sales are approved on their own, and any that don't reach the bank are flagged.",
      h1: "Automatic Credit Card Payment Verification",
      heroSubtitle:
        "Know every credit card sale actually reached your bank, automatically. Every payment is compared against the bank and confirmed, so a missed or lost POS receipt is no longer a worry.",
      intro:
        "A card sale showing in your automation doesn't mean the money settled to your bank. Shift Tracker closes that gap: it matches each card sale against the payments your bank confirms, approves the ones that settled automatically, and surfaces anything that didn't, so a card sale recorded as cash, a missing settlement, or a mismatch can't slip through.",
      featuresTitle: "How bank-confirmed card payments work",
      features: [
        {
          icon: "🔄",
          title: "Matched against bank payments",
          text: "ERP/automation (TURPAK, Asis) card sales are compared, side by side, against the card payments your bank actually confirms.",
        },
        {
          icon: "✅",
          title: "Automatic approval",
          text: "Card sales that settle to the bank are matched and approved on their own; most shifts need no manual steps.",
        },
        {
          icon: "🚩",
          title: "Unsettled or mismatched: flagged",
          text: "A card sale that never reached the bank, or whose amount doesn't match, is surfaced for review instead of quietly passing.",
        },
        {
          icon: "💳",
          title: "Payment-type correction",
          text: "A sale wrongly entered as cash can be corrected to card so it enters the verification workflow and matches the bank.",
        },
        {
          icon: "🧾",
          title: "Allocation-aware evidence",
          text: "Bank-confirmed card amounts are tracked separately, each tied to a real bank payment as audit evidence.",
        },
        {
          icon: "🔒",
          title: "You stay in control",
          text: "Admins can always review and adjust; the automation assists and keeps a full audit trail.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "How are card payments verified?",
          answer:
            "Shift Tracker compares the card sales from your ERP/automation against the card payments your bank confirms, and automatically approves each sale that has a matching bank payment.",
        },
        {
          question: "What does it catch?",
          answer:
            "Card sales that never settled to the bank, amount mismatches, and sales recorded under the wrong payment type: surfaced for review the same shift.",
        },
        {
          question: "Does it work with TURPAK or Asis?",
          answer:
            "Yes. It pulls card sales from pump automation and reconciles them against your bank-confirmed payments.",
        },
        {
          question: "Do I stay in control?",
          answer:
            "Yes. Admins can review and adjust any match by hand; the automation only assists and preserves the audit trail.",
        },
        {
          question: "How does this relate to deposit verification?",
          answer:
            "Together they give end-to-end, bank-verified shifts: card sales confirmed against bank payments, and cash deposits confirmed against your bank statement.",
        },
      ],
      ctaTitle: "Verify every card sale against the bank",
      ctaText:
        "See how bank-confirmed card payments complete your end-to-end shift verification. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Card Payment Verification",
    },
  },
  {
    slug: "canli-satis-takibi",
    enSlug: "live-sales-dashboard",
    tr: {
      metaTitle:
        "Canlı Satış Takibi | İstasyon Satış Paneli - Shift Tracker",
      metaDescription:
        "Akaryakıt istasyonu satışlarını canlı izleyin: açık vardiyayı gerçek zamanlı görün; KPI'ları, saatlik trendi ve yakıt/ödeme kırılımlarını görselleştirin.",
      h1: "Canlı Satış Takibi",
      heroSubtitle:
        "Açık vardiyanın satışlarını saniye saniye izleyin: ciro, hacim ve işlem sayısı canlı güncellenir; bugünün kapanmış vardiyalarıyla birleşip 'bugüne kadar' toplamı tek ekranda görselleşir.",
      intro:
        "Shift Tracker'ın canlı satış paneli, akaryakıt istasyonunuzun açık vardiyasını gerçek zamanlı gösterir. Satışlar POS'tan akarken KPI kartları, saatlik trend grafiği ve yakıt tipi / ödeme / segment kırılımları anında güncellenir; canlı (açık vardiya) ve içe aktarılmış (kapanmış) satışlar renklerle ayrışır. Filtreleyin, görselleştirin ve ekibinizle tek tıkla paylaşın. Tamamen internet üzerinden çalışan sistem sayesinde bu verilere dilediğiniz cihazdan, istediğiniz an ve her yerden erişebilirsiniz.",
      featuresTitle: "Canlı satış panelinde öne çıkanlar",
      features: [
        {
          icon: "⏱️",
          title: "Gerçek zamanlı KPI'lar",
          text: "Ciro, hacim, işlem sayısı ve ortalama fiş canlı güncellenir; birkaç saniyede bir yenilenir, aralığı siz seçersiniz.",
        },
        {
          icon: "📈",
          title: "Saatlik trend görselleştirme",
          text: "Satışların saat saat seyri; canlı (kırmızı) ve içe aktarılmış (mavi) katkı yığılmış olarak ayrışır.",
        },
        {
          icon: "🍩",
          title: "Kırılım grafikleri",
          text: "Yakıt tipi, ödeme tipi, pompa ve müşteri segmenti bazında pasta/çubuk kırılımları.",
        },
        {
          icon: "🧮",
          title: "“Bugüne kadar” toplamı",
          text: "Bugünün kapanmış (içe aktarılmış) vardiyaları ile canlı açık vardiya tek toplamda birleşir; çift sayım olmaz.",
        },
        {
          icon: "🔎",
          title: "Filtrelenebilir satış tablosu",
          text: "Satışları her sütuna göre filtreleyin; canlı ve içe aktarılmış satırlar etiketlerle net ayrışır.",
        },
        {
          icon: "📤",
          title: "Tek tıkla paylaşım",
          text: "Anlık satış özetini görsel olarak operasyon ekibi ve personelle paylaşın.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Canlı satış takibi nedir?",
          answer:
            "Açık vardiyanın satışlarını gerçek zamanlı gösteren bir paneldir. KPI'lar, trend ve kırılımlar POS verisi aktıkça anında güncellenir.",
        },
        {
          question: "Veriler ne sıklıkla güncellenir?",
          answer:
            "Canlı taraf birkaç saniyede bir (3-5 sn) otomatik yenilenir ve yenileme aralığını kendiniz seçebilirsiniz; sekme arka plandayken duraklatılır.",
        },
        {
          question: "Canlı ve kapanmış satışlar nasıl ayrışır?",
          answer:
            "Canlı (açık vardiya) kırmızı, içe aktarılmış (kapanmış) mavi olarak grafikte ve tabloda etiketlerle ayrışır; 'bugüne kadar' görünümü ikisini birleştirir.",
        },
        {
          question: "Canlı rakamlar kesin mi?",
          answer:
            "Canlı veriler geçici bir önizlemedir; nihai rakamlar vardiya kapanıp banka onaylı olarak içe aktarıldığında kesinleşir.",
        },
      ],
      ctaTitle: "İstasyonunuzun satışlarını canlı izleyin",
      ctaText:
        "Gerçek zamanlı KPI'ları, trend ve kırılımları kendi istasyonunuzun verisiyle görün. Shift Tracker ile istasyonunuz her zaman kontrolünüz altında.",
      ctaButton: "Demo talep edin",
      serviceName: "Canlı Satış Takibi",
    },
    en: {
      metaTitle:
        "Live Sales Dashboard | Fuel Station Sales - Shift Tracker",
      metaDescription:
        "Track your fuel station's sales live: see the open shift in real time and visualize KPIs, the hourly trend and fuel/payment breakdowns.",
      h1: "Live Sales Dashboard",
      heroSubtitle:
        "Watch the open shift's sales second by second: revenue, volume and transaction count update live, and combine with today's closed shifts into one 'today so far' view on a single screen.",
      intro:
        "Shift Tracker's live sales dashboard shows your fuel station's open shift in real time. As sales stream from the POS, the KPI cards, the hourly trend chart and the fuel-type / payment / segment breakdowns update instantly; live (open shift) and imported (closed) sales are separated by colour. Filter it, visualize it, and share it with your team in one tap. Because the whole system runs over the internet, you can reach all this data from any device, anytime, anywhere.",
      featuresTitle: "What stands out in the live dashboard",
      features: [
        {
          icon: "⏱️",
          title: "Real-time KPIs",
          text: "Revenue, volume, transaction count and average ticket update live, refreshing every few seconds at an interval you choose.",
        },
        {
          icon: "📈",
          title: "Visualized hourly trend",
          text: "How sales move hour by hour, with the live (red) and imported (blue) contribution shown stacked apart.",
        },
        {
          icon: "🍩",
          title: "Breakdown charts",
          text: "Pie/bar breakdowns by fuel type, payment type, pump and customer segment.",
        },
        {
          icon: "🧮",
          title: "“Today so far” total",
          text: "Today's closed (imported) shifts and the live open shift combine into one total, no double counting.",
        },
        {
          icon: "🔎",
          title: "Filterable sales table",
          text: "Filter sales by any column; live and imported rows are clearly separated with labels.",
        },
        {
          icon: "📤",
          title: "One-tap sharing",
          text: "Share a snapshot of sales as an image with your operations team and employees.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "What is live sales tracking?",
          answer:
            "It's a dashboard that shows the open shift's sales in real time. KPIs, the trend and breakdowns update instantly as POS data streams in.",
        },
        {
          question: "How often does the data refresh?",
          answer:
            "The live side auto-refreshes every few seconds (3-5s) and you can pick the interval yourself; it pauses while the tab is in the background.",
        },
        {
          question: "How are live and closed sales distinguished?",
          answer:
            "Live (open shift) is red and imported (closed) is blue, separated with labels in both the chart and the table; the 'today so far' view combines the two.",
        },
        {
          question: "Are the live figures final?",
          answer:
            "Live data is a provisional preview; final figures are settled once the shift closes and is imported as bank-confirmed.",
        },
      ],
      ctaTitle: "Track your station's sales live",
      ctaText:
        "See the real-time KPIs, trend and breakdowns on your own station's data. With Shift Tracker, your station is always under your control.",
      ctaButton: "Request a demo",
      serviceName: "Live Sales Dashboard",
    },
  },
  {
    slug: "tank-stok-takibi",
    enSlug: "fuel-tank-monitoring",
    tr: {
      metaTitle:
        "Tank Stok Takibi | Canlı Tank Seviyesi - Shift Tracker",
      metaDescription:
        "Akaryakıt tank stoğunu canlı izleyin: her tankın dolum yüzdesini ve güncel hacmini, yakıt bazında kalan stoğu, tüketim ve teslimat geçmişini görün.",
      h1: "Tank Stok Takibi",
      heroSubtitle:
        "Her tankı dolan bir hazne gibi görün: dolum yüzdesi ve güncel litre canlı güncellenir, yakıt bazında kalan stok tek bakışta belli olur; siparişi rahatça verirsiniz.",
      intro:
        "Shift Tracker'ın tank stok paneli, akaryakıt istasyonunuzun tanklarını gerçek zamanlı görselleştirir. Her tank, yakıt rengiyle dolan bir hazne olarak dolum yüzdesi ve güncel hacmiyle gösterilir; yakıt tipi bazında 'sipariş verilecek' canlı stok rakamı öne çıkar. Güncel stok, vardiya sonu sayımından canlı satışların düşülmesiyle hesaplanan bir tahmini defter değeridir: sipariş kararı için yeterli, anlık olarak etiketlenir. Vardiya bazlı geçmiş ise tüketim, teslimat ve stok seyrinin kesin kaydıdır.",
      featuresTitle: "Tank stok panelinde öne çıkanlar",
      features: [
        {
          icon: "🛢️",
          title: "Tankları görsel olarak izleyin",
          text: "Her tank, yakıt rengiyle dolan bir hazne; dolum yüzdesi ve güncel litre tek bakışta okunur.",
        },
        {
          icon: "⛽",
          title: "Yakıt bazında kalan stok",
          text: "Her yakıt tipi için canlı kalan stok: siparişi buna göre verirsiniz; düşük seviye belirginleşir.",
        },
        {
          icon: "⏱️",
          title: "Canlı yenileme",
          text: "Stok dakikada, 5 dakikada ya da saniye saniye yenilenir; aralığı siz seçer, dilediğinizde duraklatırsınız.",
        },
        {
          icon: "📉",
          title: "Tüketim ve stok trendi",
          text: "Vardiya bazında tüketim, teslimat ve stok seyri; yakıt başına tek çizgi ile sunulur.",
        },
        {
          icon: "🚚",
          title: "Teslimat geçmişi",
          text: "Her tanker dolumu vardiya geçmişine işlenir; teslimat hacmini ve sonrasını net görürsünüz.",
        },
        {
          icon: "📋",
          title: "Vardiya bazlı kesin kayıt",
          text: "Her tank için vardiya sonu stok, önceki stok, tüketim ve teslimat: denetime hazır tablo.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Tank stok takibi nedir?",
          answer:
            "Akaryakıt tanklarınızın güncel stoğunu dolum yüzdesi ve litre olarak canlı gösteren bir paneldir; yakıt bazında kalan stoğu ve vardiya bazlı geçmişi bir arada sunar.",
        },
        {
          question: "Güncel stok nasıl hesaplanır?",
          answer:
            "Güncel stok, son vardiya sonu sayımından açık vardiyadaki canlı satışların düşülmesiyle bulunan tahmini bir defter değeridir; tanktaki anlık fiziksel ölçüm (ATG) değil, sipariş kararı için yeterli bir göstergedir.",
        },
        {
          question: "Veriler ne sıklıkla güncellenir?",
          answer:
            "Güncel panel düzenli aralıklarla (dakika, 5 dakika ya da saniye saniye) yenilenir; aralığı kendiniz seçer, sekme arka plandayken duraklatabilirsiniz.",
        },
        {
          question: "Tanker dolumu stoğa nasıl yansır?",
          answer:
            "Vardiya ortasındaki dolum canlı tahmine bir sonraki vardiya sonu sayımına kadar yansımaz; ancak ilgili vardiyanın geçmiş kaydında teslimat hacmi olarak kesin biçimde görünür.",
        },
      ],
      ctaTitle: "Tank stoğunuzu canlı izleyin",
      ctaText:
        "Her tankın dolum yüzdesini, güncel hacmini ve yakıt bazında kalan stoğu kendi istasyonunuzun verisiyle görün. İstasyonunuzun tüm verilerine dilediğiniz cihazdan, her yerden tek panelden erişin.",
      ctaButton: "Demo talep edin",
      serviceName: "Tank Stok Takibi",
    },
    en: {
      metaTitle:
        "Fuel Tank Monitoring | Live Stock & Levels - Shift Tracker",
      metaDescription:
        "Monitor fuel tank stock live: see each tank's fill percentage and current volume, remaining stock per fuel, and per-shift consumption and delivery history.",
      h1: "Fuel Tank Monitoring",
      heroSubtitle:
        "See every tank as a filling vessel: the fill percentage and current litres update live, the remaining stock per fuel is clear at a glance, and ordering becomes easy.",
      intro:
        "Shift Tracker's tank stock dashboard visualizes your fuel station's tanks in real time. Each tank is shown as a vessel filling in its fuel colour, with its fill percentage and current volume; the live 'order-against' stock per fuel type stands out. The current stock is an estimated book value (the shift-end reading minus live sales) good enough to drive ordering decisions and labelled as indicative. The per-shift history is the exact record of consumption, deliveries and stock over time.",
      featuresTitle: "What stands out in the tank dashboard",
      features: [
        {
          icon: "🛢️",
          title: "Visualize tanks at a glance",
          text: "Each tank is a vessel filling in its fuel colour; the fill percentage and current litres read instantly.",
        },
        {
          icon: "⛽",
          title: "Remaining stock per fuel",
          text: "Live remaining stock for every fuel type: the number you order against; low levels stand out.",
        },
        {
          icon: "⏱️",
          title: "Live refresh",
          text: "Stock refreshes every minute, every 5 minutes or second by second; you pick the interval and can pause it.",
        },
        {
          icon: "📉",
          title: "Consumption & stock trend",
          text: "Per-shift consumption, deliveries and stock over time, presented as one line per fuel.",
        },
        {
          icon: "🚚",
          title: "Delivery history",
          text: "Every tanker drop lands in the shift history; you see the delivered volume and what follows clearly.",
        },
        {
          icon: "📋",
          title: "Exact per-shift record",
          text: "Shift-end stock, previous stock, consumption and delivery per tank: an audit-ready table.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "What is tank stock monitoring?",
          answer:
            "It's a dashboard that shows your fuel tanks' current stock live, as a fill percentage and in litres, alongside the remaining stock per fuel and the per-shift history.",
        },
        {
          question: "How is the current stock calculated?",
          answer:
            "Current stock is an estimated book value: the latest shift-end reading minus the live sales of the open shift. It's not a live in-tank (ATG) measurement, but it's accurate enough to drive ordering decisions.",
        },
        {
          question: "How often does the data refresh?",
          answer:
            "The current panel refreshes at a regular interval (every minute, every 5 minutes or second by second); you choose the interval and can pause it while the tab is in the background.",
        },
        {
          question: "How does a tanker delivery show up in the stock?",
          answer:
            "A mid-shift delivery isn't reflected in the live estimate until the next shift-end reading, but it shows up precisely as the delivery volume in that shift's history row.",
        },
      ],
      ctaTitle: "Monitor your tank stock live",
      ctaText:
        "See each tank's fill percentage, current volume and the remaining stock per fuel on your own station's data. Reach all your station's data from any device, anywhere, on a single panel.",
      ctaButton: "Request a demo",
      serviceName: "Fuel Tank Monitoring",
    },
  },
  {
    slug: "akaryakit-siparis-onerisi",
    enSlug: "fuel-order-suggestions",
    tr: {
      metaTitle:
        "Akaryakıt Sipariş Önerisi | Talep Tahmini - Shift Tracker",
      metaDescription:
        "Ne kadar akaryakıt sipariş etmelisiniz? Tanker varışına kadarki satış tahmin edilir; her tank için önerilen sipariş miktarı ve tükenme riski gösterilir.",
      h1: "Akaryakıt Sipariş Önerisi",
      heroSubtitle:
        "Tanker geldiğinde tankın ne kadar dolu olacağını tahmin edip güvenli doluluğa kadar ne sipariş etmeniz gerektiğini söyler; tükenme riski olan tankları acil olarak işaretler.",
      intro:
        "Shift Tracker'ın sipariş önerisi paneli, her tank için tanker varışına kadar beklenen satışı (son haftaların gün-saat ortalamasından) tahmin eder, varıştaki stok seviyesini hesaplar ve güvenli doluluk hedefine kadar 'şimdi ne kadar sipariş et' rakamını verir. Tanker varış tarihini girerek çok günlük / tatil dönemi siparişlerini de planlayabilir, tükenme riski olan tankları anında görebilirsiniz. Bu bir tahmin/öneridir; nihai karar sizindir.",
      featuresTitle: "Sipariş önerisi panelinde öne çıkanlar",
      features: [
        {
          icon: "🛒",
          title: "Tank başına sipariş miktarı",
          text: "Her tank için güvenli doluluğa kadar önerilen litre; tanker kompartıman ölçüsüne yuvarlanır.",
        },
        {
          icon: "📊",
          title: "Talep tahmini",
          text: "Son haftaların gün-saat ortalamasıyla tanker varışına kadar beklenen satışı tahmin eder.",
        },
        {
          icon: "🚨",
          title: "Tükenme uyarısı",
          text: "Tanker gelmeden bitebilecek tankları acil olarak işaretler; varıştaki negatif stoğu gösterir.",
        },
        {
          icon: "🚚",
          title: "Varış tarihi planlaması",
          text: "Tanker varış tarih-saatini girin; çok günlük / tatil dönemi siparişi otomatik büyür.",
        },
        {
          icon: "⛽",
          title: "Yakıt bazında özet",
          text: "Tank kırılımının yanında her yakıt tipi için toplam önerilen sipariş.",
        },
        {
          icon: "🔄",
          title: "Canlı güncelleme",
          text: "Satışlar geldikçe stok ve öneriler birlikte güncellenir; tank paneliyle aynı ritimde yenilenir.",
        },
      ],
      faqTitle: "Sık sorulan sorular",
      faq: [
        {
          question: "Sipariş önerisi nasıl hesaplanır?",
          answer:
            "Her tank için: varıştaki stok = güncel stok − tanker varışına kadar beklenen satış. Önerilen sipariş, bu seviyeyi güvenli doluluk hedefine (kapasitenin belirli bir oranı) tamamlayacak litredir.",
        },
        {
          question: "Talep tahmini neye dayanır?",
          answer:
            "Son haftaların gün-saat ortalamasına; yani yoğun saatleri ve haftalık ritmi yansıtır. İstatistiksel bir tahmindir, garanti değildir; yeni istasyonlarda veri biriktikçe isabet artar.",
        },
        {
          question: "Tanker varış tarihini neden giriyorum?",
          answer:
            "Varış ne kadar uzaksa o aralıkta o kadar çok satış olur. Tarihi girince öneri, o güne kadarki tüm beklenen talebi kapsayacak şekilde büyür; tatil/çok günlük kapanışlar için idealdir.",
        },
        {
          question: "Kapasitesi bilinmeyen tank ne olur?",
          answer:
            "Güvenli doluluk hedefi kapasiteye bağlı olduğundan, kapasite bilinmiyorsa sipariş rakamı hesaplanmaz; yine de tükenme riskini ve varıştaki stoğu görürsünüz. Tank kapasitesini girince öneri otomatik gelir.",
        },
      ],
      ctaTitle: "Ne kadar sipariş vereceğinizi tahmine bırakmayın",
      ctaText:
        "Kendi istasyonunuzun verisiyle tank başına önerilen sipariş ve tükenme uyarılarını görün. Güvene değil, kontrole dayalı istasyon yönetimi için Shift Tracker.",
      ctaButton: "Demo talep edin",
      serviceName: "Akaryakıt Sipariş Önerisi",
    },
    en: {
      metaTitle:
        "Fuel Order Suggestions | How Much to Order - Shift Tracker",
      metaDescription:
        "Know how much fuel to order for your station: forecast sales until the tanker arrives and see the recommended order per tank plus a run-dry risk warning.",
      h1: "Fuel Order Suggestions",
      heroSubtitle:
        "Projects how full each tank will be when the tanker arrives and tells you how much to order up to a safe-fill target, flagging tanks at risk of running dry as urgent.",
      intro:
        "Shift Tracker's order-suggestions panel forecasts each tank's expected sales until the tanker arrives (from a day-of-week × hour-of-day average of recent weeks), projects the level at arrival, and gives you the 'order this much now' number to reach a safe-fill target. Enter a tanker arrival date to plan multi-day / holiday orders, and spot run-dry risks instantly. It's an estimate / advisory; the final call is yours.",
      featuresTitle: "What stands out in the order panel",
      features: [
        {
          icon: "🛒",
          title: "Order amount per tank",
          text: "Recommended litres to reach a safe fill for each tank, rounded up to tanker compartment sizes.",
        },
        {
          icon: "📊",
          title: "Demand forecast",
          text: "Projects sales until the tanker arrives from a day-of-week × hour-of-day average of recent weeks.",
        },
        {
          icon: "🚨",
          title: "Run-dry warning",
          text: "Flags tanks that may run out before the tanker arrives; shows the negative projected stock.",
        },
        {
          icon: "🚚",
          title: "Arrival planning",
          text: "Enter a tanker arrival date-time; the multi-day / holiday order grows automatically.",
        },
        {
          icon: "⛽",
          title: "Per-fuel summary",
          text: "A total recommended order per fuel type alongside the per-tank breakdown.",
        },
        {
          icon: "🔄",
          title: "Live updates",
          text: "Stock and suggestions update together as sales arrive, refreshing in step with the tank panel.",
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          question: "How is the order suggestion calculated?",
          answer:
            "For each tank: projected stock at arrival = current stock − expected sales until the tanker arrives. The recommended order is the litres needed to top that up to a safe-fill target (a fraction of capacity).",
        },
        {
          question: "What is the demand forecast based on?",
          answer:
            "A day-of-week × hour-of-day average of recent weeks, so it reflects rush hours and the weekly rhythm. It's a statistical estimate, not a guarantee; accuracy improves as a new station accumulates data.",
        },
        {
          question: "Why do I enter a tanker arrival date?",
          answer:
            "The further out the arrival, the more sales happen in that window. Entering the date grows the suggestion to cover all expected demand until then, ideal for holiday or multi-day closures.",
        },
        {
          question: "What about a tank with unknown capacity?",
          answer:
            "Because the safe-fill target depends on capacity, the order amount isn't computed when capacity is unknown, but you still see the run-dry risk and the projected stock. Set the tank capacity and the suggestion appears automatically.",
        },
      ],
      ctaTitle: "Stop guessing how much to order",
      ctaText:
        "See the recommended order per tank and run-dry warnings on your own station's data. Station management built on control, not trust, that's Shift Tracker.",
      ctaButton: "Request a demo",
      serviceName: "Fuel Order Suggestions",
    },
  },
];

export function getLandingBySlug(slug?: string): LandingPageContent | undefined {
  return landingPages.find((p) => p.slug === slug);
}

/** Resolve a landing page by the URL slug for the active language. */
export function getLandingForLang(
  slug: string,
  isEnglish: boolean
): LandingPageContent | undefined {
  return landingPages.find((p) => (isEnglish ? p.enSlug : p.slug) === slug);
}
